import { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getResourceApiKeys } from 'js/lib/ResourceUtils';
import { Parameter, Header } from './useParameterManager';
import type { ApiRequestModalMode } from '../../../context/ApiRequestModal/ApiRequestModalContext';

export interface FormData {
  requestMethod: string;
  baseUrl: string;
  route: string;
  apiKey: string;
  pathParameters: Parameter[];
  queryParameters: Parameter[];
  headers: Header[];
  rawJsonBody?: string;
  generatedSchema?: string;
}

export interface ValidationErrors {
  pathParameters: { [id: number]: string };
  queryParameters: { [id: number]: string };
  headers: { [id: number]: string };
}

export interface UIState {
  pathParamsOpen: boolean;
  queryParamsOpen: boolean;
  headersOpen: boolean;
  bodyOpen: boolean;
}

export interface ResponseState {
  responseStatus: string;
  responseData: string;
  responseHeaders: { [key: string]: string };
  responseTab: string;
}

const defaultUIState: UIState = {
  pathParamsOpen: false,
  queryParamsOpen: false,
  headersOpen: false,
  bodyOpen: false,
};

const defaultResponseState: ResponseState = {
  responseStatus: '',
  responseData: '',
  responseHeaders: {},
  responseTab: 'body',
};

const defaultValidationErrors: ValidationErrors = {
  pathParameters: {},
  queryParameters: {},
  headers: {},
};

const safeJsonStringify = (obj: unknown): string => {
  if (!obj) return '{}';
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return '{}';
  }
};

export const useApiRequestForm = (
  datasetId: number | null,
  mode: ApiRequestModalMode,
  sourceId?: number,
  method?: string,
) => {
  const [formData, setFormData] = useState<FormData>({
    requestMethod: '',
    baseUrl: '',
    route: '',
    apiKey: '',
    pathParameters: [],
    queryParameters: [],
    headers: [],
  });

  const [originalData, setOriginalData] = useState<FormData | null>(null);
  const [uiState, setUIState] = useState<UIState>(defaultUIState);
  const [responseState, setResponseState] = useState<ResponseState>(defaultResponseState);
  const [validationErrors, setValidationErrors] =
    useState<ValidationErrors>(defaultValidationErrors);
  const currentDatasetIdRef = useRef<number | null>(null);
  const currentModeRef = useRef<ApiRequestModalMode | null>(null);
  const apiKeys = useSelector((state: any) => state.entities.apiKeys);
  const sources = useSelector((state: any) => state.entities.sources);
  const dataSets = useSelector((state: any) => state.entities.dataSets);
  const dataset = datasetId ? dataSets[datasetId] : null;

  // find duplicate keys in a params and headers
  const findDuplicateKeys = useCallback((items: (Parameter | Header)[]) => {
    const errors: { [id: number]: string } = {};
    const keyMap = new Map<string, number[]>();

    items.forEach((item) => {
      const key = item.key?.trim();
      if (key) {
        if (!keyMap.has(key)) {
          keyMap.set(key, []);
        }
        keyMap.get(key)!.push(item.id);
      }
    });

    // mark duplicates (keep the first occurrence, mark the rest as errors)
    keyMap.forEach((ids, key) => {
      if (ids.length > 1) {
        ids.slice(1).forEach((id) => {
          errors[id] = `Duplicate key: "${key}"`;
        });
      }
    });

    return errors;
  }, []);

  // check for blank keys in parameters and headers
  const findBlankKeys = useCallback((items: (Parameter | Header)[]) => {
    const errors: { [id: number]: string } = {};

    items.forEach((item) => {
      const key = item.key?.trim();
      if (!key || key === '') {
        errors[item.id] = 'Key is required';
      }
    });

    return errors;
  }, []);

  // check for reserved header prefix 'x-nexla'
  const findReservedHeaderPrefixes = useCallback((headers: Header[]) => {
    const errors: { [id: number]: string } = {};

    headers.forEach((header) => {
      const key = header.key?.trim();
      if (key && key.toLowerCase().startsWith('x-nexla')) {
        errors[header.id] = `Key cannot start with 'X-Nexla'`;
      }
    });

    return errors;
  }, []);

  useEffect(() => {
    const pathDuplicateErrors = findDuplicateKeys(formData.pathParameters);
    const pathBlankErrors = findBlankKeys(formData.pathParameters);
    const pathErrors = { ...pathDuplicateErrors, ...pathBlankErrors };

    const queryDuplicateErrors = findDuplicateKeys(formData.queryParameters);
    const queryBlankErrors = findBlankKeys(formData.queryParameters);
    const queryErrors = { ...queryDuplicateErrors, ...queryBlankErrors };

    const headerDuplicateErrors = findDuplicateKeys(formData.headers);
    const headerBlankErrors = findBlankKeys(formData.headers);
    const headerReservedErrors = findReservedHeaderPrefixes(formData.headers);
    const headerErrors = {
      ...headerDuplicateErrors,
      ...headerBlankErrors,
      ...headerReservedErrors,
    };

    setValidationErrors({
      pathParameters: pathErrors,
      queryParameters: queryErrors,
      headers: headerErrors,
    });
  }, [
    formData.pathParameters,
    formData.queryParameters,
    formData.headers,
    findDuplicateKeys,
    findBlankKeys,
    findReservedHeaderPrefixes,
  ]);

  const hasValidationErrors = useCallback(() => {
    return Object.values(validationErrors).some((errors) => Object.keys(errors).length > 0);
  }, [validationErrors]);

  const clearValidationErrors = useCallback(() => {
    setValidationErrors(defaultValidationErrors);
  }, []);

  // initialize form data when modal opens, changes mode, or dataset changes
  useEffect(() => {
    if (dataset && datasetId && (mode === 'config' || mode === 'test')) {
      const shouldRefreshData =
        currentDatasetIdRef.current !== datasetId || currentModeRef.current !== mode;

      if (shouldRefreshData) {
        currentDatasetIdRef.current = datasetId;
        currentModeRef.current = mode;

        let baseUrl = '';
        let apiKey = '';
        const route = dataset?.endpoint_spec?.route || '';

        if (sourceId) {
          const source = sources[sourceId];
          if (source) {
            const sourceApiKeys = getResourceApiKeys(source, apiKeys);
            if (sourceApiKeys) {
              const { url, api_key } = Object.values(sourceApiKeys)[0] as any;
              baseUrl = url;
              apiKey = api_key;
            }
          }
        }

        const newFormData: FormData = {
          requestMethod: method || dataset?.endpoint_spec?.method || '',
          baseUrl,
          route,
          apiKey,
          pathParameters: (dataset?.endpoint_spec?.path_params || []).map(
            (p: any, index: number) => ({
              id: index,
              key: p.key || '',
              value: p.value || '',
              required: p.required || false,
            }),
          ),
          queryParameters: (dataset?.endpoint_spec?.query_params || []).map(
            (p: any, index: number) => ({
              id: index,
              key: p.key || '',
              value: p.value || '',
              required: p.required || false,
            }),
          ),
          headers: (dataset?.endpoint_spec?.headers || []).map((h: any, index: number) => ({
            id: index,
            key: h.key || '',
            value: h.value || '',
            required: h.required || false,
          })),
          ...(dataset?.endpoint_spec?.method !== 'GET' && {
            rawJsonBody: safeJsonStringify(dataset?.endpoint_spec?.body),
            generatedSchema: '',
          }),
        };

        setFormData(newFormData);
        setOriginalData(newFormData);
        setResponseState(defaultResponseState);
        setValidationErrors(defaultValidationErrors);
      }
    }
  }, [mode, datasetId]);

  // Reset refs when modal closes
  useEffect(() => {
    if (!datasetId) {
      currentDatasetIdRef.current = null;
      currentModeRef.current = null;
    }
  }, [datasetId]);

  const updateFormData = useCallback((updates: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateUIState = useCallback((updates: Partial<UIState>) => {
    setUIState((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateResponseState = useCallback((updates: Partial<ResponseState>) => {
    setResponseState((prev) => ({ ...prev, ...updates }));
  }, []);

  const setRawJsonBody = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, rawJsonBody: value }));
  }, []);

  const setGeneratedSchema = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, generatedSchema: value }));
  }, []);

  const hasUnsavedChanges = useCallback(() => {
    if (!originalData) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  const resetOriginalData = useCallback(() => {
    setOriginalData(formData);
  }, [formData]);

  return {
    formData,
    uiState,
    responseState,
    validationErrors,
    dataset,
    updateFormData,
    updateUIState,
    updateResponseState,
    setRawJsonBody,
    setGeneratedSchema,
    hasUnsavedChanges,
    hasValidationErrors,
    clearValidationErrors,
    resetOriginalData,
  };
};
